import { useState, useEffect, useCallback, useRef } from 'react';
import { onrampApi } from '@/api/onramp';
import { offrampApi } from '@/api/offramp';

export interface CoinbaseTransaction {
  id: string;
  transactionId: string;
  status: 'created' | 'in_progress' | 'success' | 'failed';
  eventType: string;
  cryptoAmount?: string;
  cryptoCurrency?: string;
  fiatAmount?: string;
  fiatCurrency?: string;
  paymentMethod?: string;
  txHash?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

interface UseCoinbaseTransactionStatusOptions {
  /** Polling interval in milliseconds (default: 5000) */
  pollInterval?: number;
  /** Max polling duration in milliseconds (default: 300000 = 5 minutes) */
  maxPollDuration?: number;
  /** Callback when transaction succeeds */
  onSuccess?: (transaction: CoinbaseTransaction) => void;
  /** Callback when transaction fails */
  onFailed?: (transaction: CoinbaseTransaction) => void;
}

interface UseCoinbaseTransactionStatusResult {
  /** Current transaction status */
  transaction: CoinbaseTransaction | null;
  /** Whether we're actively polling */
  isPolling: boolean;
  /** Current status (null if no transaction) */
  status: 'created' | 'in_progress' | 'success' | 'failed' | null;
  /** Any error that occurred */
  error: string | null;
  /** Start polling */
  startPolling: () => void;
  /** Stop polling */
  stopPolling: () => void;
}

/**
 * Hook to poll for Coinbase transaction status
 *
 * @param type - 'onramp' for deposits, 'offramp' for withdrawals
 * @param enabled - Whether polling should be active
 * @param options - Configuration options
 */
export function useCoinbaseTransactionStatus(
  type: 'onramp' | 'offramp',
  enabled: boolean = false,
  options: UseCoinbaseTransactionStatusOptions = {}
): UseCoinbaseTransactionStatusResult {
  const {
    pollInterval = 5000, // 5 seconds
    maxPollDuration = 5 * 60 * 1000, // 5 minutes
    onSuccess,
    onFailed,
  } = options;

  const [transaction, setTransaction] = useState<CoinbaseTransaction | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use refs to track state across effect cycles
  const pollStartTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTransactionIdRef = useRef<string | null>(null);

  // Get the appropriate API based on type
  const api = type === 'onramp' ? onrampApi : offrampApi;

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.getPendingStatus();
      const tx = response.transaction as CoinbaseTransaction | null;

      if (tx) {
        setTransaction(tx);
        setError(null);

        // Check if this is a new completed transaction
        const isNewTransaction = tx.transactionId !== lastTransactionIdRef.current;
        const isTerminalState = tx.status === 'success' || tx.status === 'failed';

        if (isTerminalState) {
          // Stop polling on terminal state
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsPolling(false);

          // Trigger callbacks for new terminal transactions
          if (isNewTransaction || tx.status === 'success' || tx.status === 'failed') {
            lastTransactionIdRef.current = tx.transactionId;

            if (tx.status === 'success' && onSuccess) {
              onSuccess(tx);
            } else if (tx.status === 'failed' && onFailed) {
              onFailed(tx);
            }
          }
        }
      }
    } catch (err: any) {
      console.error(`Failed to fetch ${type} status:`, err);
      setError(err.message || 'Failed to fetch status');
    }
  }, [api, type, onSuccess, onFailed]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Already polling

    setIsPolling(true);
    setError(null);
    pollStartTimeRef.current = Date.now();
    lastTransactionIdRef.current = null;

    // Fetch immediately
    fetchStatus();

    // Then poll at interval
    intervalRef.current = setInterval(() => {
      // Check if we've exceeded max poll duration
      if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > maxPollDuration) {
        console.log(`${type} polling timed out after ${maxPollDuration}ms`);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsPolling(false);
        return;
      }

      fetchStatus();
    }, pollInterval);
  }, [fetchStatus, pollInterval, maxPollDuration, type]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    pollStartTimeRef.current = null;
  }, []);

  // Start/stop polling based on enabled prop
  useEffect(() => {
    if (enabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    transaction,
    isPolling,
    status: transaction?.status || null,
    error,
    startPolling,
    stopPolling,
  };
}
